import { Spinner, Toggle } from '@/components';
import { SpinnerSize } from '@/constants';
import type { FC, ReactNode } from 'react';
import { SubText, TextContainer, Title, ToggleItemContainer, ToggleItemContent } from './ToggleItem.style';

interface ToggleItemProps {
  title: string;
  subText?: string;
  toggled: boolean;
  isToggling?: boolean;
  disabled?: boolean;
  popup?: ReactNode;
  onToggle: () => void;
  stateSource?: 'internal' | 'external';
}

/**
 * A component that combines a toggle switch with text content and optional popup
 * @param {Object} props - Component props
 * @param {string} props.title - Main text to display
 * @param {string} [props.subText] - Secondary text to display below title
 * @param {boolean} props.toggled - Whether the toggle is in the on state
 * @param {boolean} [props.isToggling=false] - Whether the toggle is in a loading state
 * @param {boolean} [props.disabled=false] - Whether the toggle is disabled
 * @param {ReactNode} [props.popup] - Optional popup content to render below the toggle
 * @param {() => void} props.onToggle - Callback function when toggle is clicked
 */
const ToggleItem: FC<ToggleItemProps> = ({
  title,
  subText,
  toggled,
  disabled,
  isToggling = false,
  popup,
  onToggle,
}) => {
  return (
    <ToggleItemContainer>
      <ToggleItemContent>
        <TextContainer>
          <Title>{title}</Title>
          {subText && <SubText>{subText}</SubText>}
        </TextContainer>
        {isToggling ? (
          <Spinner size={SpinnerSize.Large} />
        ) : (
          <Toggle disabled={disabled} toggled={toggled} onToggle={onToggle} />
        )}
      </ToggleItemContent>
      {popup}
    </ToggleItemContainer>
  );
};

export default ToggleItem;
